import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader } from 'lucide-react';
import { apiService } from '../services/api';
import DashboardLayout from '../components/layout/DashboardLayout';

const OAuthCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const success = searchParams.get('success');
        const error = searchParams.get('error');
        const connectionId = searchParams.get('connection_id');
        const provider = searchParams.get('provider');

        // Si succès, le backend a déjà créé la connexion
        if (success === 'true' && connectionId) {
          setStatus('success');
          setMessage('Connexion réussie ! Redirection en cours...');
          
          // Nettoyer sessionStorage
          sessionStorage.removeItem('oauth_provider');
          sessionStorage.removeItem('oauth_state');
          
          // Rediriger vers la page d'intégrations après 2 secondes
          setTimeout(() => {
            navigate('/workspace/integrations');
          }, 2000);
          return;
        }

        // Si erreur
        if (error) {
          setStatus('error');
          setMessage(decodeURIComponent(error) || 'Erreur lors de l\'authentification. Veuillez réessayer.');
          setTimeout(() => {
            navigate('/workspace/integrations');
          }, 3000);
          return;
        }

        // Si code présent (ancien flux pour compatibilité)
        const code = searchParams.get('code');
        if (code) {
          const state = searchParams.get('state');
          let providerName = sessionStorage.getItem('oauth_provider') || 'google-drive';
          
          if (state) {
            const parts = state.split('_');
            if (parts.length > 0) {
              providerName = parts[0];
            }
          }

          const result = await apiService.handleOAuthCallback(code, state || undefined, providerName);

          if (result.success) {
            setStatus('success');
            setMessage('Connexion réussie ! Redirection en cours...');
            
            sessionStorage.removeItem('oauth_provider');
            sessionStorage.removeItem('oauth_state');
            
            setTimeout(() => {
              navigate('/workspace/integrations');
            }, 2000);
          } else {
            setStatus('error');
            setMessage(result.message || 'Erreur lors de la connexion.');
            setTimeout(() => {
              navigate('/workspace/integrations');
            }, 3000);
          }
          return;
        }

        // Aucun paramètre valide
        setStatus('error');
        setMessage('Paramètres de callback invalides.');
        setTimeout(() => {
          navigate('/workspace/integrations');
        }, 3000);
      } catch (err: any) {
        console.error('Erreur callback OAuth:', err);
        setStatus('error');
        setMessage(err?.response?.data?.error || err?.message || 'Erreur lors de la connexion.');
        setTimeout(() => {
          navigate('/workspace/integrations');
        }, 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <DashboardLayout>
      <div className="flex items-center justify-center min-h-screen">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          {status === 'loading' && (
            <>
              <Loader className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Connexion en cours...
              </h2>
              <p className="text-gray-600">
                Veuillez patienter pendant que nous finalisons votre connexion.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Connexion réussie !
              </h2>
              <p className="text-gray-600">
                {message}
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Erreur de connexion
              </h2>
              <p className="text-gray-600">
                {message}
              </p>
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default OAuthCallbackPage;

